'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { PostSummary } from '@/lib/mdx';
import { BookOpen } from 'lucide-react';

interface PostHeatmapProps {
  posts: PostSummary[];
}

export function PostHeatmap({ posts }: PostHeatmapProps) {
  const [hoveredPost, setHoveredPost] = useState<PostSummary | null>(null);

  // Map each post to a color and height for the heatmap bar
  const heatmapData = posts.map((post, i) => {
    // Generate a pseudo-random height between 30% and 100% based on title length
    const heightPercent = 30 + (post.meta.title.length % 70);
    // Cycle through some brand colors
    const colors = [
      'bg-primary', 'bg-secondary', 'bg-accent', 
      'bg-[#2dd4bf]', 'bg-[#9f7aea]', 'bg-brand-dark', 'bg-[#6a3c9e]'
    ];
    const colorClass = colors[i % colors.length];

    return { post, heightPercent, colorClass };
  });

  // Duplicate to make a long scrolling or filled bar if we don't have many posts
  // But let's just use what we have and maybe duplicate a few times for visual effect
  // since Hex has a lot of bars.
  const visualBars = [...heatmapData, ...heatmapData, ...heatmapData, ...heatmapData].slice(0, 80);

  return (
    <div className='relative w-full hidden md:block'>
      <div className='flex items-end justify-between gap-[1px] h-20 w-full overflow-hidden relative group'>
        {visualBars.map((item, idx) => (
          <Link
            key={`${item.post.slug}-${idx}`}
            href={`/blog/${item.post.slug}`}
            onMouseEnter={() => setHoveredPost(item.post)}
            onMouseLeave={() => setHoveredPost(null)}
            className={`flex-1 transition-all duration-300 hover:brightness-150 cursor-pointer opacity-80 hover:opacity-100 ${item.colorClass}`}
            style={{ height: `${item.heightPercent}%` }}
          />
        ))}
      </div>

      {/* Hover Card */}
      {hoveredPost && (
        <div className='absolute top-full left-1/2 -translate-x-1/2 mt-4 z-50 animate-in fade-in zoom-in duration-200'>
          <div className='bg-card/90 backdrop-blur-xl border border-border/20 shadow-2xl rounded-xl p-4 w-80 pointer-events-none'>
            <div className='relative h-40 w-full mb-4 rounded-md overflow-hidden'>
              {hoveredPost.meta.image ? (
                <Image
                  src={hoveredPost.meta.image.src}
                  alt={hoveredPost.meta.title}
                  fill
                  className='object-cover'
                />
              ) : (
                <div className='flex h-full w-full items-center justify-center bg-primary/10'>
                  <BookOpen className='text-primary/40 size-8' />
                </div>
              )}
            </div>
            <div className='font-mono text-xs text-muted-foreground uppercase tracking-widest mb-2 flex items-center gap-2'>
              <span>{hoveredPost.meta.date}</span>
            </div>
            <h4 className='font-sans text-lg font-bold leading-tight mb-2 text-foreground'>
              {hoveredPost.meta.title}
            </h4>
            <div className='font-mono text-xs text-muted-foreground'>
              Author/ <span className='text-foreground'>{hoveredPost.meta.author}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
